import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import Spinner from "../../components/ui/spinner";
import { usePrototypesQuery } from "../../data/queries/prototypes";
import { useAdminLoginMutation, useDeletePrototypeMutation } from "../../data/mutations/prototypes";
import { PrototypeListView } from "./PrototypeListView";
import { UploadView } from "./UploadView";
import { SuccessView } from "./SuccessView";
import { TekkrIcon } from "../../components/shared/tekkr/tekkr-logo";

type ViewState = "login" | "list" | "upload" | "success";

export default function AdminPage() {
    const [viewState, setViewState] = useState<ViewState>("login");
    const [adminPassword, setAdminPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [currentSlug, setCurrentSlug] = useState("");
    const [currentViewerPassword, setCurrentViewerPassword] = useState("");

    const { data: prototypesData, isLoading } = usePrototypesQuery(viewState !== "login" ? adminPassword : null);
    const adminLogin = useAdminLoginMutation();
    const deletePrototype = useDeletePrototypeMutation();

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoginError("");
        try {
            await adminLogin.mutateAsync(adminPassword);
            setViewState("list");
        } catch {
            setLoginError("Invalid password. Please try again.");
        }
    };

    if (viewState === "login") {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <TekkrIcon className="mx-auto mb-4 h-12 w-12" />
                        <CardTitle className="text-2xl">Tekkr Prototypes</CardTitle>
                        <p className="text-sm text-muted-foreground">Admin access</p>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="password">Admin Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={adminPassword}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setAdminPassword(e.target.value)
                                    }
                                    placeholder="Enter admin password"
                                    required
                                />
                                {loginError && <p className="text-sm text-destructive">{loginError}</p>}
                            </div>
                            <Button type="submit" className="w-full" disabled={adminLogin.isPending}>
                                {adminLogin.isPending ? (
                                    <>
                                        <Spinner className="mr-2 h-4 w-4" /> Signing in...
                                    </>
                                ) : (
                                    "Sign in"
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="container mx-auto max-w-4xl p-6">
            {viewState === "list" && (
                <PrototypeListView
                    prototypes={prototypesData?.data ?? []}
                    isLoading={isLoading}
                    onUpload={() => setViewState("upload")}
                    onDelete={async (slug) => {
                        if (window.confirm("Are you sure you want to delete this prototype?")) {
                            await deletePrototype.mutateAsync({ adminPassword, slug });
                        }
                    }}
                />
            )}

            {viewState === "upload" && (
                <UploadView
                    adminPassword={adminPassword}
                    onBack={() => setViewState("list")}
                    onSuccess={(slug, password) => {
                        setCurrentSlug(slug);
                        setCurrentViewerPassword(password);
                        setViewState("success");
                    }}
                />
            )}

            {viewState === "success" && (
                <SuccessView slug={currentSlug} password={currentViewerPassword} onBack={() => setViewState("list")} />
            )}
        </div>
    );
}
